import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';

import { EditFile, ViewFile } from 'contest/components/brief/file';
import Dropzone from 'contest/components/brief/uploader/dropzone';
import FileInput from 'contest/components/brief/uploader/file-input';
import { Matrix, MatrixItem } from 'contest/components/matrix';
import Button from '@99designs/react-components/components/Button';
import Icon from '@99designs/react-components/components/Icon';

import { connectReadElement, RawHtml } from 'contest/components/brief/read-element';

// Filter out all values that don't have a matching file record.
const validFiles = element =>
    element.value.filter(value => element.metadata.files[value.publicId] != null);

const DownloadAllButton = ({ downloadAllLink }) => (
    <Button
        href={downloadAllLink}
        variations={['primary']}
        icon={<Icon variations={['download-1']} />}
        iconAlignment="left"
    >
        <FormattedMessage id="brief_file_download_all" />
    </Button>
);

DownloadAllButton.propTypes = {
    downloadAllLink: PropTypes.string.isRequired,
};

const ReadFilesElement = ({ files, downloadAllLink }) => (
    <div>
        <Matrix large={3} variations={['matrix--separated']}>
            {files.map((file, i) => (
                <MatrixItem key={file.publicId}>
                    <ViewFile file={file.file}>
                        <RawHtml html={file.formattedComment} />
                    </ViewFile>
                </MatrixItem>
            ))}
        </Matrix>
        <DownloadAllButton downloadAllLink={downloadAllLink} />
    </div>
);
ReadFilesElement.propTypes = {
    files: PropTypes.array.isRequired,
    downloadAllLink: PropTypes.string.isRequired,
};
export const ViewFilesElement = connectReadElement(
    ReadFilesElement,
    element => ({
        files: validFiles(element).map(({ publicId }) => ({
            file: element.metadata.files[publicId],
            publicId: publicId,
            formattedComment:
                element.metadata.formattedText != null && element.metadata.formattedText[publicId]
                    ? element.metadata.formattedText[publicId]
                    : '',
        })),
        downloadAllLink: element.metadata.downloadAllLink,
    }),
    element => validFiles(element).length
);

export class EditFilesElement extends React.Component {
    constructor(props) {
        super(props);

        this.onFileInputChange = this.onFileInputChange.bind(this);
        this.onProcessingDroppedFilesComplete = this.onProcessingDroppedFilesComplete.bind(this);
    }

    onFileInputChange(onChangeEvent) {
        const file = onChangeEvent.target;
        this.props.addUploads(file);
    }

    onProcessingDroppedFilesComplete(files) {
        this.props.addUploads(files);
    }

    shouldComponentUpdate(nextProps, nextState) {
        return (
            this.props.files !== nextProps.files ||
            this.props.uploads !== nextProps.uploads ||
            this.state !== nextState
        );
    }

    attachmentRemoveHandler(publicId) {
        return () => {
            this.props.onFileRemove(publicId);
        };
    }

    uploadRemoveHandler(uploadid) {
        return () => {
            this.props.onUploadRemove(uploadid);
        };
    }

    attachmentCommentChangeHandler(publicId) {
        return comment => {
            this.props.onFileCommentChange(publicId, comment);
        };
    }

    uploadCommentChangeHandler(uploadid) {
        return comment => {
            this.props.onUploadCommentChange(uploadid, comment);
        };
    }

    render() {
        return (
            <Dropzone
                multiple
                onProcessingDroppedFilesComplete={this.onProcessingDroppedFilesComplete}
            >
                <Matrix large={3} variations={['matrix--separated']}>
                    {this.props.files.map(attachment => (
                        <MatrixItem key={attachment.publicId}>
                            <EditFile
                                file={attachment}
                                comment={attachment.comment}
                                placeholder={this.props.placeholder}
                                onRemove={this.attachmentRemoveHandler(attachment.publicId)}
                                onChange={this.attachmentCommentChangeHandler(attachment.publicId)}
                                onBlur={this.props.onSave}
                            />
                        </MatrixItem>
                    ))}

                    {this.props.uploads.map(upload => (
                        <MatrixItem key={upload.uploadid}>
                            <EditFile
                                file={upload}
                                progress={upload.progress}
                                comment={upload.comment}
                                placeholder={this.props.placeholder}
                                error={upload.error}
                                onRemove={this.uploadRemoveHandler(upload.uploadid)}
                                onChange={this.uploadCommentChangeHandler(upload.uploadid)}
                                onBlur={this.props.onSave}
                                retryUpload={this.props.retryUpload}
                            />
                        </MatrixItem>
                    ))}

                    <MatrixItem key="upload-button">
                        <div className="brief-file-drop-target">
                            <div className="brief-file-overlay">
                                <div className="brief-file-overlay__content">
                                    <div className="brief-file-overlay__icon brief-file-overlay__icon--lighter">
                                        <Icon variations={['plus-2', '3x']} />
                                    </div>
                                    <div className="brief-file-overlay__content__label">
                                        <FormattedMessage id="uploader_drop_target_text" />
                                    </div>
                                </div>
                                <div className="brief-file-overlay__outline" />
                                <FileInput onChange={this.onFileInputChange} />
                            </div>
                        </div>
                    </MatrixItem>
                </Matrix>
                <DownloadAllButton downloadAllLink={this.props.downloadAllLink} />
            </Dropzone>
        );
    }
}
EditFilesElement.propTypes = {
    files: PropTypes.arrayOf(
        PropTypes.shape({
            publicId: PropTypes.string.isRequired,
            thumb: PropTypes.string,
            name: PropTypes.string,
            size: PropTypes.number.isRequired,
            time: PropTypes.number,
            comment: PropTypes.string,
        })
    ).isRequired,
    uploads: PropTypes.arrayOf(
        PropTypes.shape({
            publicId: PropTypes.string, // optional
            uploadid: PropTypes.number.isRequired,
            thumb: PropTypes.string,
            name: PropTypes.string.isRequired,
            size: PropTypes.number.isRequired,
            time: PropTypes.number,
            comment: PropTypes.string,
        })
    ).isRequired,
    addUploads: PropTypes.func.isRequired,
    onFileRemove: PropTypes.func.isRequired,
    onUploadRemove: PropTypes.func.isRequired,
    onFileCommentChange: PropTypes.func.isRequired,
    onUploadCommentChange: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    retryUpload: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    downloadAllLink: PropTypes.string.isRequired,
};
EditFilesElement.defaultProps = {
    placeholder: '',
};



// WEBPACK FOOTER //
// ./src/web/js/contest/components/brief/files-element.jsx